import { useState, useEffect } from "react";
import ProjectTable from "../components/ProjectTable";
import DeleteIcon from "@mui/icons-material/Delete";
import { observer } from "mobx-react";
import RestoreIcon from "@mui/icons-material/Restore";
import { Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { useSnackbar } from "notistack";
import ProjectStore, { useProjectStore } from "../model/ProjectStore";
import { CircularProgress } from "@mui/material";

const loadProjects = (managedProject, resultHandler) => {
  let getAPI = "/api/project/manageProject";
  if (process.env.NODE_ENV !== "production") {
    getAPI = "http://localhost:7071" + getAPI;
  }
  managedProject(true);
  fetch(getAPI, {
    method: "GET",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (!response.ok) {
        response
          .text()
          .then((text) => {
            throw Error(response.statusText + ": " + text);
          })
          .catch((error) =>
            this.props.enqueueSnackbar(error.message, { variant: "error" })
          );
      } else {
        response.json().then((result) => {
          resultHandler(result);
        });
        managedProject(false);
      }
    })
    .catch((error) => this.setState({ open: true }));
};

const updateArchive = (project, id, archived) => {
  let getAPI = "/api/project/manage/" + id;
  if (process.env.NODE_ENV !== "production") {
    getAPI = "http://localhost:7071" + getAPI;
  }
  fetch(getAPI, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (!response.ok) {
        response
          .text()
          .then((text) => {
            throw Error(response.statusText + ": " + text);
          })
          .catch((error) =>
            this.props.enqueueSnackbar(error.message, { variant: "error" })
          );
      } else {
        response.json().then((result) => {
          let updProject = ProjectStore.fromJS(result);
          if (updProject.id === id) {
            project.currentHash = updProject.currentHash;
            project.isArchive = updProject.isArchive;
          }
        });
        archived(true);
      }
    })
    .catch((error) => this.setState({ open: true }));
};

const ManageProjectView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const project = useProjectStore();
  const [archiveStarted, setArchiveStarted] = useState(false);
  const [archiveCompleted, setArchiveCompleted] = useState(false);
  const [managingProject, setManagingProject] = useState(false);
  const [archive, setArchive] = useState([]);
  const [current, setCurrent] = useState([]);
  const archiveKey = "archive";
  const activeKey = "active";

  const sortHeadersCurrent = [
    { id: "move", disablePadding: false, label: "Archive Project" },
    { id: "name", disablePadding: true, label: "Project Name" },
    { id: "time", disablePadding: true, label: "Last Saved" },
  ];
  const sortHeadersArchive = [
    { id: "move", disablePadding: false, label: "Restore Project" },
    { id: "name", disablePadding: true, label: "Project Name" },
    { id: "time", disablePadding: true, label: "Last Saved" },
  ];

  useEffect(() => {
    project.checkProjUpdate(project.id, project.currentHash, enqueueSnackbar)
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        if (error.cause === "opaqueredirect") {
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      });

    loadProjects(setManagingProject, (result) => {
      const arch = result.filter((x) => x.isArchived);
      const curr = result.filter((x) => !x.isArchived);
      setArchive(
        arch.map((x) => {
          return {
            name: x.name,
            data: x.project,
            time: x.time,
          };
        })
      );

      setCurrent(
        curr.map((x) => {
          return {
            name: x.name,
            data: x.project,
            time: x.time,
          };
        })
      );
    });
  }, [enqueueSnackbar, project]);

  const moveCurrent = (projectId) => {
    setArchiveStarted(true);
    updateArchive(project, projectId, setArchiveCompleted);
    const tableElement = current.filter((x) => x.data === projectId)[0];
    archive.push(tableElement);
    const newCurrent = current.filter((x) => x !== tableElement);
    setCurrent(newCurrent);
  };

  const moveArchive = (projectId) => {
    setArchiveStarted(true);
    updateArchive(project, projectId, setArchiveCompleted);
    const tableElement = archive.filter((x) => x.data === projectId)[0];
    current.push(tableElement);
    const newArchive = archive.filter((x) => x !== tableElement);
    setArchive(newArchive);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setArchiveStarted(false);
    setArchiveCompleted(false);
  };

  return (
    <div>
      <div style={{ display: "flex", marginLeft: 8 }}>
        <ProjectTable
          key={activeKey}
          tableName="Active Projects"
          data={current}
          sortHeaders={sortHeadersCurrent}
          moveIcon={<DeleteIcon />}
          move={moveCurrent}
        />

        <ProjectTable
          key={archiveKey}
          tableName="Archive Projects"
          data={archive}
          sortHeaders={sortHeadersArchive}
          moveIcon={<RestoreIcon />}
          move={moveArchive}
        />

        <Snackbar
          title="Archiving Started"
          open={archiveStarted}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="info"
            sx={{ width: "100%" }}
          >
            Project's archive status is being changed...
          </Alert>
        </Snackbar>
        <Snackbar
          title="Archiving Successul"
          open={archiveCompleted}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{ width: "100%" }}
          >
            Project's archive status is successfully updated!
          </Alert>
        </Snackbar>
        <Snackbar
          title="Manage Project Message"
          open={managingProject}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="info"
            sx={{ width: "100%" }}
          >
            <div>
              <CircularProgress color="inherit" />
              Please wait while the projects you own are being fetched, this may
              take several minutes
            </div>
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default observer(ManageProjectView);
